<script lang="ts">
import { defineComponent, PropType, inject } from 'vue'
import CheckboxItem from '@/components/form/CheckboxItem.vue'

export default defineComponent({
  name: 'NewsletterSetup',

  components: {
    CheckboxItem,
  },

  props: {
    namespace: {
      type: String,
      default: '',
    },

    inputOptionalNewsletterInfo: {
      type: Object as PropType<OptionalNewsletterSettings>,
      required: true,
    },

    hasBorder: {
      type: Boolean,
      default: true,
    },
  },

  emits: [
    'update:mailStopFlag',
    'update:mailNewsStopFlag',
    'update:mailExpoStopFlag',
    'update:mailAiStopFlag',
    'update:mailPr1StopFlag',
    'update:mailPr2StopFlag',
    'update:mailPr3StopFlag',
  ],

  setup() {
    const MAIL_STOP_CATEGORIES = inject<Record<string, { title: string, desc: string }>>('MAIL_STOP_CATEGORIES')!

    return {
      // constants
      MAIL_STOP_CATEGORIES,
    }
  }
})
</script>

<template>
  <div
    :class="[
      'o-list__newsletterSetup',
      { 'is-border': hasBorder },
    ]">
    <dl
      v-if="inputOptionalNewsletterInfo.mailStopFlag"
      class="o-list__newsletterSetup--unit">
      <dt class="o-list__newsletterSetup--term">
        <!--
          (注)
          サーバから受け取る値が「stop flag」なので、
          true = メルマガ停止、false = メルマガ受信
          となるため、チェックボックスの挙動と逆になる。
        -->
        <CheckboxItem
          :model-value="inputOptionalNewsletterInfo.mailStopFlag.value"
          :namespace="namespace"
          :name="inputOptionalNewsletterInfo.mailStopFlag.name"
          :validation-scheme="inputOptionalNewsletterInfo.mailStopFlag.validationScheme"
          :value="false"
          :false-value="true"
          :label="MAIL_STOP_CATEGORIES.mail_stop_flg.title"
          @update:model-value="$emit('update:mailStopFlag', $event)" />
      </dt>
      <dd class="o-list__newsletterSetup--description">
        <span class="o-list__newsletterSetup--label">
          {{ MAIL_STOP_CATEGORIES.mail_stop_flg.desc }}
        </span>
      </dd>
    </dl>

    <dl
      v-if="inputOptionalNewsletterInfo.mailNewsStopFlag"
      class="o-list__newsletterSetup--unit">
      <dt class="o-list__newsletterSetup--term">
        <CheckboxItem
          :model-value="inputOptionalNewsletterInfo.mailNewsStopFlag.value"
          :namespace="namespace"
          :name="inputOptionalNewsletterInfo.mailNewsStopFlag.name"
          :validation-scheme="inputOptionalNewsletterInfo.mailNewsStopFlag.validationScheme"
          :value="false"
          :false-value="true"
          :label="MAIL_STOP_CATEGORIES.mail_news_stop_flg.title"
          @update:model-value="$emit('update:mailNewsStopFlag', $event)" />
      </dt>
      <dd class="o-list__newsletterSetup--description">
        <span class="o-list__newsletterSetup--label">
          {{ MAIL_STOP_CATEGORIES.mail_news_stop_flg.desc }}
        </span>
      </dd>
    </dl>

    <dl
      v-if="inputOptionalNewsletterInfo.mailExpoStopFlag"
      class="o-list__newsletterSetup--unit">
      <dt class="o-list__newsletterSetup--term">
        <CheckboxItem
          :model-value="inputOptionalNewsletterInfo.mailExpoStopFlag.value"
          :namespace="namespace"
          :name="inputOptionalNewsletterInfo.mailExpoStopFlag.name"
          :validation-scheme="inputOptionalNewsletterInfo.mailExpoStopFlag.validationScheme"
          :value="false"
          :false-value="true"
          :label="MAIL_STOP_CATEGORIES.mail_expo_stop_flg.title"
          @update:model-value="$emit('update:mailExpoStopFlag', $event)" />
      </dt>
      <dd class="o-list__newsletterSetup--description">
        <span class="o-list__newsletterSetup--label">
          {{ MAIL_STOP_CATEGORIES.mail_expo_stop_flg.desc }}
        </span>
      </dd>
    </dl>

    <dl
      v-if="inputOptionalNewsletterInfo.mailAiStopFlag"
      class="o-list__newsletterSetup--unit">
      <dt class="o-list__newsletterSetup--term">
        <CheckboxItem
          :model-value="inputOptionalNewsletterInfo.mailAiStopFlag.value"
          :namespace="namespace"
          :name="inputOptionalNewsletterInfo.mailAiStopFlag.name"
          :validation-scheme="inputOptionalNewsletterInfo.mailAiStopFlag.validationScheme"
          :value="false"
          :false-value="true"
          :label="MAIL_STOP_CATEGORIES.mail_ai_stop_flg.title"
          @update:model-value="$emit('update:mailAiStopFlag', $event)" />
      </dt>
      <dd class="o-list__newsletterSetup--description">
        <span class="o-list__newsletterSetup--label">
          {{ MAIL_STOP_CATEGORIES.mail_ai_stop_flg.desc }}
        </span>
      </dd>
    </dl>

    <dl
      v-if="inputOptionalNewsletterInfo.mailPr2StopFlag"
      class="o-list__newsletterSetup--unit">
      <dt class="o-list__newsletterSetup--term">
        <CheckboxItem
          :model-value="inputOptionalNewsletterInfo.mailPr2StopFlag.value"
          :namespace="namespace"
          :name="inputOptionalNewsletterInfo.mailPr2StopFlag.name"
          :validation-scheme="inputOptionalNewsletterInfo.mailPr2StopFlag.validationScheme"
          :value="false"
          :false-value="true"
          :label="MAIL_STOP_CATEGORIES.mail_pr2_stop_flg.title"
          @update:model-value="$emit('update:mailPr2StopFlag', $event)" />
      </dt>
      <dd class="o-list__newsletterSetup--description">
        <span class="o-list__newsletterSetup--label">
          {{ MAIL_STOP_CATEGORIES.mail_pr2_stop_flg.desc }}
        </span>
      </dd>
    </dl>

    <dl
      v-if="inputOptionalNewsletterInfo.mailPr1StopFlag"
      class="o-list__newsletterSetup--unit">
      <dt class="o-list__newsletterSetup--term">
        <CheckboxItem
          :model-value="inputOptionalNewsletterInfo.mailPr1StopFlag.value"
          :namespace="namespace"
          :name="inputOptionalNewsletterInfo.mailPr1StopFlag.name"
          :validation-scheme="inputOptionalNewsletterInfo.mailPr1StopFlag.validationScheme"
          :value="false"
          :false-value="true"
          :label="MAIL_STOP_CATEGORIES.mail_pr1_stop_flg.title"
          @update:model-value="$emit('update:mailPr1StopFlag', $event)" />
      </dt>
      <dd class="o-list__newsletterSetup--description">
        <span class="o-list__newsletterSetup--label">
          {{ MAIL_STOP_CATEGORIES.mail_pr1_stop_flg.desc }}
        </span>
      </dd>
    </dl>

    <dl
      v-if="inputOptionalNewsletterInfo.mailPr3StopFlag"
      class="o-list__newsletterSetup--unit">
      <dt class="o-list__newsletterSetup--term">
        <CheckboxItem
          :model-value="inputOptionalNewsletterInfo.mailPr3StopFlag.value"
          :namespace="namespace"
          :name="inputOptionalNewsletterInfo.mailPr3StopFlag.name"
          :validation-scheme="inputOptionalNewsletterInfo.mailPr3StopFlag.validationScheme"
          :value="false"
          :false-value="true"
          :label="MAIL_STOP_CATEGORIES.mail_pr3_stop_flg.title"
          @update:model-value="$emit('update:mailPr3StopFlag', $event)" />
      </dt>
      <dd class="o-list__newsletterSetup--description">
        <span class="o-list__newsletterSetup--label">
          {{ MAIL_STOP_CATEGORIES.mail_pr3_stop_flg.desc }}
        </span>
      </dd>
    </dl>
  <!-- /.o-list__newsletterSetup --></div>
</template>

<style lang="sass" scoped>
.o-list__newsletterSetup
  $root: &
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--unit
    margin-top: 1em

    &:first-child
      margin-top: 0

  & &--term
    line-height: 1.5
    font-weight: bold
    margin-top: 1em

    &:first-child
      margin-top: 0

  & &--description
    // normalize のチェックボックスを基準としているので、具体的な変数の値は normalize（≒ mixins.sass の checkboxStyle）を参照
    // CheckboxItem のマージン間と揃える
    $icon-size-px: 22
    $icon-size-em: math.div($icon-size-px, $font-normal-px)
    $margin-size: 0.5em
    margin-top: 0.2em
    padding-left: ($icon-size-em + $margin-size)

    &:first-child
      margin-top: 0

  & &--label
    font-size: $font-small

  &.is-border
    $gap: 20

    #{$root}--unit
      margin-top: 0
      padding: changeSizePx2Em($gap) 0
      border-top: 1px solid $border-x-light-gray

      &:first-child
        padding-top: 0
        border-top: none

      &:last-child
        padding-bottom: 0
</style>
